import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Model, { Providers } from "@tripian/model";
import { providers } from "@tripian/core";
import { saveNotification } from "../redux/action/user";
import useReservation from "./useReservation";
import moment from "moment";
import useTranslate from "./useTranslate";

export const defaultBookingConfirmRequest: Providers.Viator.BookingConfirmRequest =
  {
    cartRef: "",
    bookerInfo: {
      firstName: "",
      lastName: "",
    },
    communication: {
      email: "",
      phone: "",
    },
    items: [],
    paymentToken: "",
  };

// Destinations cache constants
const DESTINATIONS_CACHE_KEY = "viator_destinations_cache";
const DESTINATIONS_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

const useViatorApi = (
  cityId?: number,
  cityName?: string,
  arrivalDatetime?: string,
  departureDatetime?: string
) => {
  const [viatorBookingInfo, setViatorBookingInfo] =
    useState<Providers.Viator.BookingConfirm>();
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [availableDate, setAvailableDate] = useState<string>(
    arrivalDatetime
      ? moment(arrivalDatetime).format("YYYY-MM-DD")
      : moment().add(1, "days").format("YYYY-MM-DD")
  );
  const [selectedHour, setSelectedHour] = useState<string>("");
  const [locations, setLocations] = useState<Providers.Viator.GroupedLocations>(
    {
      start: [],
      end: [],
      pickup: [],
    }
  );
  const [loadings, setLoadings] = useState<
    | "loadingViatorProductInfo"
    | "loadingViatorProductAvailability"
    | "loadingBookingHold"
    | "loadingBookingConfirm"
    | "loadingViatorLocations"
    | ""
  >("");

  const [viatorProductInfo, setViatorProductInfo] =
    useState<Providers.Viator.TourData>();

  const [viatorProductAvailability, setViatorProductAvailability] =
    useState<Providers.Viator.AvailabilityCheck>();

  const [successfullBooking, setSuccessfullBooking] = useState<boolean>(false);

  const [paymentSessionToken, setPaymentSessionToken] = useState<string>();

  const [bookingConfirmRequest, setBookingConfirmRequest] =
    useState<Providers.Viator.BookingConfirmRequest>(
      defaultBookingConfirmRequest
    );

  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const [paymentFailed, setPaymentFailed] = useState<boolean>(false);

  const [exchangeRates, setExchangeRates] = useState<
    Providers.Viator.ExchangeRates[]
  >([]);

  const [viatorDestinations, setViatorDestinations] = useState<
    Providers.Viator.Destinations[]
  >([]);
  const [loadingDestinations, setLoadingDestinations] = useState<boolean>(false);

  const { reservationAdd } = useReservation(cityId);

  const dispatch = useDispatch();

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const exchangeRatesTimerRef = useRef<NodeJS.Timeout | null>(null);
  const confirmInFlightRef = useRef(false);

  const { t } = useTranslate();

  const fetchLocations = useCallback(
    async (
      pickupLocRefs?: string[],
      startLocRefs?: string[],
      endLocRefs?: string[]
    ): Promise<void> => {
      if (
        providers.viator &&
        window.tconfig.SHOW_TOURS_AND_TICKETS &&
        window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
          x => x === Model.PROVIDER_ID.VIATOR
        )
      ) {
        setLoadings("loadingViatorLocations");
        const groupedPickupLocations: Providers.Viator.Location[] = [];
        const groupedStartLocations: Providers.Viator.Location[] = [];
        const groupedEndLocations: Providers.Viator.Location[] = [];

        const allLocRefs = [
          ...(pickupLocRefs || []),
          ...(startLocRefs || []),
          ...(endLocRefs || []),
        ];

        // Helper function for batching requests
        const fetchBatchedLocations = async (
          locRefs: string[]
        ): Promise<Providers.Viator.Location[]> => {
          const batchedResults: Providers.Viator.Location[] = [];
          const batchSize = 300;

          for (let i = 0; i < locRefs.length; i += batchSize) {
            const slicedRefs = locRefs.slice(i, i + batchSize);
            try {
              const response = await providers.viator?.locations(slicedRefs);
              if (response) batchedResults.push(...response.locations);
            } catch (error: any) {
              dispatch(
                saveNotification(
                  Model.NOTIFICATION_TYPE.ERROR,
                  "viatorFetchLocationsError",
                  "Unable to load pickup locations."
                )
              );
            }
          }

          return batchedResults;
        };

        try {
          // Fetch all locations
          if (allLocRefs.length > 0) {
            const allResults = await fetchBatchedLocations(allLocRefs);

            // Separate results based on the provided references
            allResults.forEach(location => {
              if (pickupLocRefs?.includes(location.reference)) {
                groupedPickupLocations.push(location);
              }
              if (startLocRefs?.includes(location.reference)) {
                groupedStartLocations.push(location);
              }
              if (endLocRefs?.includes(location.reference)) {
                groupedEndLocations.push(location);
              }
            });
          }

          setLocations({
            start: groupedStartLocations,
            end: groupedEndLocations,
            pickup: groupedPickupLocations,
          });
        } catch (error: any) {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "viatorFetchLocationsError",
              "Unable to load pickup locations."
            )
          );
        } finally {
          setLoadings("");
        }
      }
    },
    [dispatch]
  );

  const fetchProductInfo = useCallback(
    (productCode: string): Promise<Boolean> => {
      if (
        providers.viator &&
        window.tconfig.SHOW_TOURS_AND_TICKETS &&
        window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
          x => x === Model.PROVIDER_ID.VIATOR
        )
      ) {
        setLoadings("loadingViatorProductInfo");
        setViatorProductInfo(undefined);

        return providers.viator
          .tourData(productCode)
          .then((data: Providers.Viator.TourData) => {
            // Check if response is an error (e.g., 503 Service Unavailable)
            if (!data || (data as any).code || (data as any).status >= 400) {
              dispatch(
                saveNotification(
                  Model.NOTIFICATION_TYPE.ERROR,
                  "viatorFetchTourError",
                  "Unable to load tour details. Please try again later."
                )
              );
              setLoadings("");
              return false;
            }

            // Validate that required data structure exists
            if (!data.info || !data.info.logistics) {
              dispatch(
                saveNotification(
                  Model.NOTIFICATION_TYPE.ERROR,
                  "viatorFetchTourError",
                  "Tour information is temporarily unavailable."
                )
              );
              setLoadings("");
              return false;
            }

            const pickupLocRefs =
              data.info.logistics.travelerPickup?.locations?.map(
                l => l.location.ref
              );
            const startLocRefs = data.info.logistics.start?.map(
              l => l.location.ref
            );
            const endLocRefs = data.info.logistics.end?.map(
              l => l.location.ref
            );
            fetchLocations(pickupLocRefs, startLocRefs, endLocRefs);

            if (
              window.tconfig.PROVIDERS.tourAndTicket.find(
                t => t.id === Model.PROVIDER_ID.VIATOR
              )?.prod === false
            ) {
              setViatorProductInfo(data);
            } else {
              setViatorProductInfo(data);
            }
            return true;
          })
          .catch(() => {
            dispatch(
              saveNotification(
                Model.NOTIFICATION_TYPE.ERROR,
                "viatorFetchTourError",
                "Unable to load tour details. Please try again later."
              )
            );
            setLoadings("");
            return false;
          })
          .finally(() => {
            setLoadings("");
          });
      }
      return Promise.resolve(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const fetchProductAvailability = useCallback(
    async (
      request: Providers.Viator.AvailabilityCheckRequest
    ): Promise<boolean> => {
      if (
        !providers.viator ||
        !window.tconfig.SHOW_TOURS_AND_TICKETS ||
        !window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
          x => x === Model.PROVIDER_ID.VIATOR
        )
      ) {
        return false;
      }

      setLoadings("loadingViatorProductAvailability");
      try {
        const res = await providers.viator.availabilityPriceCheck(request);

        if (!res) {
          setViatorProductAvailability({
            bookableItems: [],
            currency: request.currency,
            productCode: request.productCode,
            travelDate: request.travelDate,
          });
          return false;
        }

        if ((res as any).code) {
          setViatorProductAvailability({
            bookableItems: [],
            currency: request.currency,
            productCode: request.productCode,
            travelDate: request.travelDate,
          });
          return false;
        }

        setViatorProductAvailability(res as Providers.Viator.AvailabilityCheck);
        return true;
      } catch (error) {
        setViatorProductAvailability({
          bookableItems: [],
          currency: request.currency,
          productCode: request.productCode,
          travelDate: request.travelDate,
        });
        return false;
      } finally {
        setLoadings("");
      }
    },
    []
  );

  const fetchExchangeRates = useCallback(
    async (sourceCurrencies: string[], targetCurrencies: string[]) => {
      try {
        const response = await providers.viator?.exchangeRates(
          sourceCurrencies,
          targetCurrencies
        );

        if (response && response.length > 0) {
          setExchangeRates(response);

          const expiryTime = response[0]?.expiry;
          if (expiryTime) {
            const expiryMoment = moment(expiryTime);
            const now = moment();

            if (expiryMoment.isAfter(now)) {
              const delay = expiryMoment.diff(now);

              if (exchangeRatesTimerRef.current) {
                clearTimeout(exchangeRatesTimerRef.current);
              }

              exchangeRatesTimerRef.current = setTimeout(() => {
                fetchExchangeRates(sourceCurrencies, targetCurrencies);
              }, delay);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
      }
    },
    []
  );

  useEffect(() => {
    if (
      viatorProductAvailability &&
      viatorProductAvailability?.currency !== "USD"
    ) {
      fetchExchangeRates([viatorProductAvailability.currency], ["USD"]);
    }

    return () => {
      if (exchangeRatesTimerRef.current) {
        clearTimeout(exchangeRatesTimerRef.current);
      }
    };
  }, [fetchExchangeRates, viatorProductAvailability]);

  const resetTimer = useCallback((validUntilTime?: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (validUntilTime) {
      const validUntil = moment.utc(validUntilTime);
      const now = moment.utc();

      const delay = validUntil.diff(now);

      if (delay > 0) {
        timerRef.current = setTimeout(() => {
          setModalVisible(true);
        }, delay);
      } else {
        setModalVisible(true);
      }
    }
  }, []);

  const fetchProductBookingHold = useCallback(
    async (
      request: Providers.Viator.BookingCardHoldRequest
    ): Promise<Boolean> => {
      if (
        !providers.viator ||
        !window.tconfig.SHOW_TOURS_AND_TICKETS ||
        !window.tconfig.TOUR_TICKET_PROVIDER_IDS.some(
          x => x === Model.PROVIDER_ID.VIATOR
        )
      ) {
        return false;
      }

      setLoadings("loadingBookingHold");
      try {
        const data = await providers.viator.bookingCardHold(request);

        if (!data || !data.items || data.items.length === 0) {
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "viatorProductBookingHold",
              "Invalid response received"
            )
          );
          return false;
        }

        setPaymentSessionToken(data.paymentSessionToken);
        setBookingConfirmRequest(prevRequest => ({
          ...prevRequest,
          cartRef: data.cartRef,
          items: data.items.map(item => ({
            bookingRef: item.bookingRef,
          })),
        }));

        const firstItem = data.items[0];
        if (firstItem.status === "BOOKABLE") {
          const { availability, pricing } = firstItem.bookingHoldInfo;
          const validUntilTimes = [
            availability?.validUntil,
            pricing?.validUntil,
          ].filter(Boolean);

          if (validUntilTimes.length > 0) {
            const earliestValidUntil = validUntilTimes.reduce(
              (earliest, current) => {
                return moment(current).isBefore(moment(earliest))
                  ? current
                  : earliest;
              }
            );
            resetTimer(earliestValidUntil);
            setModalVisible(false);
          }
        } else if (firstItem.status === "REJECTED") {
          const reason =
            firstItem.rejectionReasonCode || "Unknown rejection reason";
          dispatch(
            saveNotification(
              Model.NOTIFICATION_TYPE.ERROR,
              "viatorProductBookingHold",
              reason
                .split("_")
                .map(
                  word =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join(" ")
            )
          );
          return false;
        }

        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred";
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "viatorProductBookingHold",
            errorMessage
          )
        );
        return false;
      } finally {
        setLoadings("");
      }
    },
    [dispatch, resetTimer]
  );

  const setBookingConfirm = (
    request: Providers.Viator.BookingConfirmRequest
  ) => {
    setBookingConfirmRequest(request);
  };

  const setBookingConfirmLoading = (loading: boolean) => {
    if (loading === true) {
      setLoadings("loadingBookingConfirm");
    } else {
      setLoadings("");
    }
  };

  const setBookingInfoModal = (show: boolean) => {
    setShowInfoModal(show);
  };

  const setPaymentFailedModal = (failed: boolean) => {
    setPaymentFailed(failed);
  };

  const changeAvailableDate = (date: string) => {
    setAvailableDate(date);
  };

  const changeSelectedHour = (hour: string) => {
    setSelectedHour(hour);
  };

  const confirmPayment = async (
    requestParam: Providers.Viator.BookingConfirmRequest
  ): Promise<boolean> => {
    if (!providers.viator) {
      dispatch(
        saveNotification(
          Model.NOTIFICATION_TYPE.ERROR,
          "viatorBookingConfirmError",
          "Viator provider is not available"
        )
      );
      return false;
    }

    const additionalBookingDetails: Providers.Viator.AdditionalBookingDetails =
      {
        fraudPreventionDetails: {
          subChannelId: window.location.origin,
          voucherDeliveryType: "EMAIL_TO_CUSTOMER",
        },
      };

    const confirmRequest = { ...requestParam, additionalBookingDetails };

    // A second confirm for the same cart fails at Viator and surfaces as an error next to the real success
    if (confirmInFlightRef.current) return false;
    confirmInFlightRef.current = true;

    try {
      const reservationInfo =
        await providers.viator.bookingConfirm(confirmRequest);

      if (
        !reservationInfo ||
        !reservationInfo.items ||
        reservationInfo.items.length === 0
      ) {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "viatorBookingConfirmError",
            // core's Viator error handler resolves with the error message instead of rejecting
            typeof reservationInfo === "string" && reservationInfo !== ""
              ? reservationInfo
              : "Invalid reservation response"
          )
        );
        return false;
      }

      const saveData: Providers.Viator.BookingReservationDetails = {
        ...reservationInfo,
        cityName: cityName || "",
        tourImage:
          viatorProductInfo?.info.images[0]?.variants[
            viatorProductInfo.info.images[0].variants.length - 1
          ]?.url || "",
        tourName: viatorProductInfo?.info.title || "",
        availableDate,
        selectedHour,
      };

      const bookingDateTime = `${availableDate} ${selectedHour}`;
      const formattedBookingDateTime = moment(bookingDateTime).format(
        "YYYY-MM-DD HH:mm:ss"
      );

      const firstItem = reservationInfo.items[0];
      if (firstItem.status === "CONFIRMED") {
        await reservationAdd(
          {
            key: Model.PROVIDER_NAME.VIATOR,
            provider: Model.PROVIDER_NAME.VIATOR,
            value: saveData,
            bookingDateTime: formattedBookingDateTime,
          },
          false
        );
        setSuccessfullBooking(true);
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.SUCCESS,
            "viatorBookingConfirmSuccess",
            t("trips.myTrips.localExperiences.tourDetails.successfullyBooked")
          )
        );
      } else if (firstItem.status === "PENDING") {
        await reservationAdd(
          {
            key: Model.PROVIDER_NAME.VIATOR,
            provider: Model.PROVIDER_NAME.VIATOR,
            value: saveData,
            bookingDateTime: formattedBookingDateTime,
          },
          false
        );
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.WARNING,
            "viatorBookingConfirmWarning",
            t(
              "trips.myTrips.localExperiences.tourDetails.bookingIsStillAwatingConfirm"
            )
          )
        );
      } else if (firstItem.status === "REJECTED") {
        dispatch(
          saveNotification(
            Model.NOTIFICATION_TYPE.ERROR,
            "viatorBookingConfirmError",
            t("trips.myTrips.localExperiences.tourDetails.bookingRejected")
          )
        );
        setPaymentFailed(true);
        return false;
      }

      setShowInfoModal(true);
      setViatorBookingInfo(reservationInfo);
      return true;
    } catch (error) {
      try {
        const bookingStatus = await providers.viator.bookingStatus({
          bookingRef: requestParam.items[0].bookingRef,
        });
        if (bookingStatus?.status === "FAILED") {
          setPaymentFailed(true);
        }
      } catch (statusError) {
        console.error("Error checking booking status:", statusError);
      }

      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      dispatch(
        saveNotification(
          Model.NOTIFICATION_TYPE.ERROR,
          "viatorBookingConfirmError",
          errorMessage
        )
      );
      return false;
    } finally {
      confirmInFlightRef.current = false;
      setLoadings("");
    }
  };

  const fetchDestinations = useCallback(async (): Promise<
    Providers.Viator.Destinations[]
  > => {
    // 1. Check cache first
    const cachedData = localStorage.getItem(DESTINATIONS_CACHE_KEY);
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const isExpired = Date.now() - timestamp > DESTINATIONS_CACHE_TTL;
        if (!isExpired && Array.isArray(data)) {
          setViatorDestinations(data);
          return data;
        }
      } catch (e) {
        localStorage.removeItem(DESTINATIONS_CACHE_KEY);
      }
    }

    // 2. API provider check
    if (!providers.viator || !window.tconfig.SHOW_TOURS_AND_TICKETS) {
      return [];
    }

    // 3. Fetch from API if cache is missing or expired
    setLoadingDestinations(true);
    try {
      const destinations = await providers.viator.destinations();

      // 4. Save to cache
      localStorage.setItem(
        DESTINATIONS_CACHE_KEY,
        JSON.stringify({
          data: destinations,
          timestamp: Date.now(),
        })
      );

      setViatorDestinations(destinations);
      return destinations;
    } catch (error: any) {
      dispatch(
        saveNotification(
          Model.NOTIFICATION_TYPE.ERROR,
          "viatorFetchDestinationsError",
          "Unable to load destinations. Please try again later."
        )
      );
      return [];
    } finally {
      setLoadingDestinations(false);
    }
  }, [dispatch]);

  const getCancellationPolicy = (
    cancellation: Providers.Viator.CancellationPolicy,
    cancellationTime: Date
  ): string => {
    const currentTime = new Date(cancellationTime).getTime();

    if (cancellation.type === "ALL_SALES_FINAL") {
      return t(
        "trips.myTrips.localExperiences.tourDetails.cancellation.allSalesFinal"
      );
    }

    if (cancellation.type === "STANDARD" || cancellation.type === "CUSTOM") {
      const eligibility = cancellation.refundEligibility.find(refund => {
        const startTimestamp = refund.startTimestamp
          ? new Date(refund.startTimestamp).getTime()
          : null;
        const endTimestamp = refund.endTimestamp
          ? new Date(refund.endTimestamp).getTime()
          : null;

        return (
          (startTimestamp === null || currentTime >= startTimestamp) &&
          (endTimestamp === null || currentTime < endTimestamp)
        );
      });

      if (eligibility) {
        if (eligibility.percentageRefundable > 0) {
          const refundDeadline = eligibility.startTimestamp
            ? moment(eligibility.startTimestamp).format(
                "h:mm A [on] MMM D, YYYY"
              )
            : null;

          if (refundDeadline) {
            return `${t("trips.myTrips.localExperiences.tourDetails.cancellation.freeCancellationBefore")} ${refundDeadline}`;
          } else {
            return t(
              "trips.myTrips.localExperiences.tourDetails.cancellation.freeCancellation"
            );
          }
        } else {
          return t(
            "trips.myTrips.localExperiences.tourDetails.cancellation.noRefundAvailable"
          );
        }
      }

      return t(
        "trips.myTrips.localExperiences.tourDetails.cancellation.refundCouldNotDetermined"
      );
    }

    return t(
      "trips.myTrips.localExperiences.tourDetails.cancellation.notRecognized"
    );
  };

  return {
    loadings,
    fetchProductInfo,
    viatorProductInfo,
    fetchProductAvailability,
    viatorProductAvailability,
    exchangeRates,
    successfullBooking,
    fetchProductBookingHold,
    paymentSessionToken,
    bookingConfirmRequest,
    setBookingConfirm,
    setBookingConfirmLoading,
    showInfoModal,
    setBookingInfoModal,
    confirmPayment,
    viatorBookingInfo,
    availableDate,
    changeAvailableDate,
    selectedHour,
    changeSelectedHour,
    locations,
    getCancellationPolicy,
    modalVisible,
    paymentFailed,
    setPaymentFailedModal,
    viatorDestinations,
    loadingDestinations,
    fetchDestinations,
  };
};

export default useViatorApi;
